<template>
  <v-app id="inspire" :style="envBackgroundStyle">
    <v-navigation-drawer
        v-model="drawer"
        app
        v-if="authenticated && $route.path !== '/'"
        expand-on-hover
        mini-variant
    >
      <v-list dense>
          <section-btn
              v-for="(menuItem, i) in menuItems"
              :key="i"
              :to="`/${menuItem.link}`"
              :icon="menuItem.icon"
              :target="menuItem.target"
          >
            {{ menuItem.text }}
          </section-btn>
      </v-list>

      <v-spacer />
      <v-divider />

      <v-subheader
          v-if="hasAnyAbility(
              $constants.ABILITY.SERVICE_TAIL_NUMBERS_REPORT,
              $constants.ABILITY.SERVICE_LOGS_TRACKING_30_M,
              $constants.ABILITY.SERVICE_LOGS_TRACKING_1_H,
              $constants.ABILITY.SERVICE_LOGS_TRACKING_1_D,
          )"
      >
        services
      </v-subheader>

      <v-list dense>
        <section-btn
            v-for="(menuItem, i) in menuServices"
            :key="i"
            :to="`/${menuItem.link}`"
            :icon="menuItem.icon"
        >
          {{ menuItem.text }}
        </section-btn>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar
        app
        color="indigo"
        dark
        :dense="true"
    >
      <v-app-bar-nav-icon
          @click.stop="drawer = !drawer"
          v-if="authenticated && $route.path !== '/'"
      >
        <v-icon v-if="!drawer">mdi-menu</v-icon>
        <v-icon v-else>mdi-close</v-icon>
      </v-app-bar-nav-icon>
      <v-toolbar-title>
        <b>Passenger Intelligence</b>Cloud
        (<span :style=" {'color':envModeColor}">
            {{ envModeName }}
            </span>)
      </v-toolbar-title>

      <v-spacer></v-spacer>
      <v-chip
          v-if="impersonation"
          color="orange darken-2"
          class="mr-4"
          label
      >
        <v-icon left small>mdi-incognito</v-icon>
        Impersonation: {{ impersonation.user_name }}
        <v-btn
            icon
            x-small
            class="ml-2"
            @click="stopImpersonation"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
              >
                mdi-close
              </v-icon>
            </template>
            stop impersonation
          </v-tooltip>
        </v-btn>
      </v-chip>
      <v-toolbar-items>
        <template v-if="authenticated && hasAllAbilities($constants.ABILITY.DEVELOPER)">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  dark
                  @click="clearBigQueryCache"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-cached</v-icon>
              </v-btn>
            </template>
            <span>Clear BigQuery cache</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  @click="clearApiCache"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-cached</v-icon>
              </v-btn>
            </template>
            <span>Clear API cache</span>
          </v-tooltip>
        </template>
        <template v-if="!authenticated">
          <v-btn icon>
            <v-icon>mdi-login-variant</v-icon>
          </v-btn>
        </template>
        <template v-else>
          <v-icon
              v-if="hasAllAbilities($constants.ABILITY.CUSTOMER)"
              small
              color="#FFD600"
          >
            mdi-airplane
          </v-icon>
          <v-btn text>{{ user.name }}</v-btn>
          <v-btn icon @click="signOut">
            <v-icon>mdi-logout-variant</v-icon>
          </v-btn>
        </template>
      </v-toolbar-items>
    </v-app-bar>

    <v-main>
      <v-layout class="fill-height" fluid no-gutters>
        <sign-in v-if="!authenticated"/>
        <keep-alive v-else>
          <router-view/>
        </keep-alive>
      </v-layout>
    </v-main>
    <v-footer color="indigo" app>
      <span class="white--text">&copy; {{ new Date().getFullYear() }}</span>
    </v-footer>

    <v-snackbar v-model="snackbar.show" :timeout="5000" :color="snackbar.color">
      {{ snackbar.text }}

      <template v-slot:action="{ attrs }">
        <v-icon
            v-bind="attrs"
            small
            @click.stop="setSnack({snack: false})"
        >
          mdi-close
        </v-icon>
      </template>
    </v-snackbar>
    <stand-by :show="overlay"/>
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SectionBtn from '../components/SectionBtn.vue';
import SignIn from '../pages/SignIn.vue';
import StandBy from '../components/StandBy.vue';

export default {
  computed: {
    ...mapGetters({
      authenticated: 'auth/authenticated',
      user: 'auth/user',
      impersonation: 'auth/impersonation',
      snack: 'snackbar/snack',
      snackColor: 'snackbar/color',
      snackText: 'snackbar/text',
      menuItems: 'menu/filteredMenuItems',
      menuServices: 'menu/filteredMenuServices',
    }),
    envModeName() {
      return process.env.VUE_APP_DEPLOYED_SITE_NAME;
    },
    envModeColor() {
      return process.env.VUE_APP_DEPLOYED_SITE_COLOR;
    },
    envUseWatermark() {
      return process.env.VUE_APP_USE_WATERMARK === 'true';
    },
    envBackgroundStyle() {
      const dateText = `${new Date().toISOString().slice(0, 10)} UTC`;
      const siteName = this.escapeSvgText(`${this.envModeName || ''} ${dateText}`.trim());
      const watermark = `
        <svg xmlns="http://www.w3.org/2000/svg" width="360" height="180" viewBox="0 0 360 180">
          <rect width="360" height="180" fill="#ffffff"/>
          <text
            x="180"
            y="100"
            text-anchor="middle"
            font-family="Arial, Helvetica, sans-serif"
            font-size="20"
            font-weight="700"
            fill="#d0d0d0"
            transform="rotate(-28 180 90)"
          >${siteName}</text>
        </svg>
      `;

      return this.envUseWatermark
        ? {
          backgroundColor: '#ffffff',
          backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(watermark)}")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '360px 180px',
        }
        : {};
    },
  },
  methods: {
    ...mapActions({
      signOutAction: 'auth/signOut',
      setSnack: 'snackbar/setState',
    }),

    async signOut() {
      await this.signOutAction();
    },

    async stopImpersonation() {
      await this.$store.dispatch('auth/stopImpersonation');
      window.location.href = this.$router.resolve({ name: 'root' }).href;
      window.location.reload();
    },

    clearBigQueryCache() {
      this.$requestHelpers
        .get('/api/admin/clear/big-query-cache')
        .then(() => {
          window.location.reload();
        });
    },

    clearApiCache() {
      this.$requestHelpers
        .get('/api/admin/clear/api-cache')
        .then(() => {
          window.location.reload();
        });
    },

    escapeSvgText(text) {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
    },
  },

  components: {
    SignIn,
    SectionBtn,
    StandBy,
  },
  props: {
    source: String,
  },

  data: () => ({
    drawer: null,
    mini: true,
    component: null,

    snackbar: {
      text: '',
      color: '',
      show: false,
    },

    overlay: false,
  }),

  created() {
    // this.redirectToFirstPage();
    this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'snackbar/SET_SNACK_STATE') {
        this.snackbar.text = this.snackText;
        this.snackbar.color = this.snackColor;
        this.snackbar.show = this.snack;
      }
      if (mutation.type === 'overlay/SHOW_OVERLAY') {
        this.overlay = state.overlay.show;
      }
    });
    document.title = 'Passenger IntelligenceCloud';
  },
};
</script>
